import { ref } from 'vue'
import { useStore } from 'vuex'
import { differenceInDays } from 'date-fns'

import bus from '@/plugins/eventbus'
import Preferences from '@/plugins/preferences'
import { useDeviceAware } from '@/composables/useDeviceAware'
import { useExtensionPresence } from '@/composables/useExtensionPresence'
import { openExternalUrl, openInSystemBrowser } from '@/utils/external-link'
import {
  MIN_ITEMS,
  MIN_ACCOUNT_AGE_DAYS,
  REASK_COOLDOWN_MS,
  COMPLETED_REASK_MS,
  IS_STAGING,
  MAX_SHOWS,
  SETTINGS_KEY,
  PENDING_TRIGGER_KEY,
  PENDING_TRIGGER_TTL_MS,
  EVENTS,
  TARGET,
  RATING_RESULT,
  RATING_TRIGGER,
  IOS_APP_ID,
  EXTENSION_BROWSERS,
} from '@/constants/ratingPrompt'

// Every client asks through the same modal (RatingPromptDialog); only the
// moment differs:
//   native            -> queued by an add, then flushed once the user is
//                        somewhere the dialog can actually be seen;
//   extension/webapp  -> asked on open. Their add flow ends by closing its own
//                        window, so there is no post-add moment to hang it on.

// Guards against two flushes racing (a route change and a resume can fire back
// to back). Per JS context, which is all it needs to cover.
let flushing = false

// Maps a browser extension-store base URL (CONFIG.storeLinks[linkKey]) to its
// "rate / leave a review" page so a positive answer lands on the review surface
// rather than the plain listing.
function reviewUrlForBrowser(linkKey, storeUrl) {
  if (!storeUrl) return storeUrl
  const base = storeUrl.replace(/\/+$/, '') // trim trailing slash for consistent joins
  switch (linkKey) {
    // Chrome Web Store: reviews tab lives at <detail>/reviews
    case 'chrome': return `${base}/reviews`
    // Firefox Add-ons (AMO): <addon>/reviews/
    case 'firefox': return `${base}/reviews/`
    // Safari extension ships via the App Store: write-review deep link
    case 'safari': return `${storeUrl}${storeUrl.includes('?') ? '&' : '?'}action=write-review`
    // Edge Add-ons has no dedicated review URL; ratings live on the detail page.
    case 'edge':
    default: return storeUrl
  }
}

// A prompt is only worth opening on a surface the user is actually looking at.
// Android's share Activity keeps running JS for a moment after MainActivity
// takes the foreground — without this the dialog would open into a WebView that
// is being destroyed, which is exactly how the prompt used to be lost.
function isSurfaceVisible() {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden'
}

// Modal surface state. The singleton <RatingPromptDialog> (mounted in App.vue,
// a DialogHybrid so it's a bottom sheet on mobile) and the flow share one
// open/await channel at module scope.
const dialogOpen = ref(false)
let dialogResolve = null
let dialogDisplayed = null

// Opens the dialog and resolves with the user's choice (RATING_RESULT.*) or
// null when dismissed. `onDisplayed` fires once the dialog has actually
// rendered — a show is recorded then, never merely because the flow asked for
// one.
function openRatingDialog(onDisplayed) {
  return new Promise(resolve => {
    dialogResolve = resolve
    dialogDisplayed = onDisplayed
    dialogOpen.value = true
  })
}

// Called by <RatingPromptDialog> on answer or dismiss. Guarded so the
// dismiss-on-close path can't double-settle after a button already answered.
function settleRatingDialog(result) {
  if (!dialogResolve) return
  const resolve = dialogResolve
  dialogResolve = null
  dialogDisplayed = null
  dialogOpen.value = false
  resolve(result)
}

// Called by <RatingPromptDialog> once it has rendered the open dialog.
function notifyRatingDialogDisplayed() {
  const onDisplayed = dialogDisplayed
  dialogDisplayed = null
  onDisplayed?.()
}

// Consumed by <RatingPromptDialog>.
export function useRatingDialog() {
  return { dialogOpen, settleRatingDialog, notifyRatingDialogDisplayed }
}

/**
 * In-app rating prompt logic. Resolves the review target for the current
 * client, gates on engagement + per-target history, and runs the native review
 * (mobile) or store redirect (extension/webapp). State lives in the backend
 * user settings, scoped per target; the queued trigger lives in device storage.
 */
export function useRatingPrompt() {
  const store = useStore()
  const { isNative, isWebApp, deviceInfo } = useDeviceAware()
  const { checkPresence } = useExtensionPresence()

  const session = () => store.getters['auth/session']

  // --- target resolution -----------------------------------------------------

  // Returns { target, storeUrl, reviewUrl } for the current client, or null when
  // there is no rateable target (e.g. plain webapp, or an unknown browser).
  // reviewUrl is where a positive answer should land the user (review page);
  // storeUrl is the plain listing used as a native fallback.
  function resolveTarget() {
    if (isNative.value) {
      const os = deviceInfo.value.platform
      if (os === 'ios') return { target: TARGET.IOS, storeUrl: CONFIG.storeLinks.ios, reviewUrl: CONFIG.storeLinks.ios }
      if (os === 'android') return { target: TARGET.ANDROID, storeUrl: CONFIG.storeLinks.android, reviewUrl: CONFIG.storeLinks.android }
      return null
    }

    // extension popup OR webapp -> the browser's extension store review page
    const browser = (deviceInfo.value.browser || '').toLowerCase()
    const match = EXTENSION_BROWSERS.find(b => browser.includes(b.match))
    if (!match) return null
    const storeUrl = CONFIG.storeLinks[match.linkKey]
    return { target: match.target, storeUrl, reviewUrl: reviewUrlForBrowser(match.linkKey, storeUrl) }
  }

  // --- engagement + history gating -------------------------------------------

  function isEligible() {
    const user = session()
    if (!user) return false
    if ((user.itemsCount ?? 0) < MIN_ITEMS) return false
    if (!user.createdAt) return false
    return differenceInDays(new Date(), new Date(user.createdAt)) >= MIN_ACCOUNT_AGE_DAYS
  }

  async function readState() {
    try {
      const settings = await store.dispatch('user/getSettings')
      const entry = settings?.find(s => s.name === SETTINGS_KEY)
      return entry?.value ? JSON.parse(entry.value) : {}
    } catch {
      return {}
    }
  }

  async function writeState(state) {
    await store.dispatch('user/editSettings', { [SETTINGS_KEY]: JSON.stringify(state) })
  }

  const elapsedSince = timestamp => Date.now() - new Date(timestamp).getTime()

  function canShowTarget(state, target) {
    const t = state[target]
    if (!t) return true
    // On staging the terminal gates are bypassed so the full flow can be tested
    // repeatedly (e.g. after opening the store link, which marks `completed`).
    // Only the short re-ask cooldown applies. Production keeps both hard stops.
    if (!IS_STAGING) {
      // A completed target reopens after COMPLETED_REASK_MS: the OS can suppress
      // the review sheet without telling us, so "completed" is not proof the
      // user ever saw one. Entries written before completedAt existed fall back
      // to lastShownAt (always written alongside), so the users who completed
      // under the old "terminal forever" rule are reached too.
      const completedAt = t.completedAt ?? t.lastShownAt
      if (t.completed && (!completedAt || elapsedSince(completedAt) < COMPLETED_REASK_MS)) return false
      if ((t.shows ?? 0) >= MAX_SHOWS) return false
    }
    if (t.lastShownAt && elapsedSince(t.lastShownAt) < REASK_COOLDOWN_MS) {
      return false
    }
    return true
  }

  // True when we may display the prompt right now for `target` (eligible, not
  // capped/completed/cooling-down). There is deliberately no once-per-session
  // rule: the cooldown is the real cadence, and a per-JS-context flag was never
  // one anyway (Android runs two WebViews, the extension one per popup).
  async function shouldPrompt(target) {
    if (!isEligible()) return false
    const state = await readState()
    return canShowTarget(state, target)
  }

  async function recordShown(target, trigger) {
    const state = await readState()
    const t = state[target] ?? { shows: 0, completed: false }
    state[target] = { ...t, shows: (t.shows ?? 0) + 1, lastShownAt: new Date().toISOString() }
    await writeState(state)
    bus.emit(EVENTS.SHOWN, { target, trigger, shows: state[target].shows })
  }

  async function markCompleted(target) {
    const state = await readState()
    state[target] = { ...(state[target] ?? { shows: 0 }), completed: true, completedAt: new Date().toISOString() }
    await writeState(state)
  }

  // --- outcomes --------------------------------------------------------------

  // Positive: native review sheet (mobile) or open the store review page
  // (ext/webapp). reviewUrl is the browser store's review surface; falls back to
  // the plain listing (storeUrl) when no review URL exists.
  //
  // Store URLs deliberately use openInSystemBrowser instead of openExternalUrl:
  // they must escape to the system browser so the OS hands off to the App Store
  // / Play app. The in-app browser would just render the store web page.
  async function accept({ target, storeUrl, reviewUrl }, trigger) {
    bus.emit(EVENTS.ACCEPTED, { target, trigger })
    await markCompleted(target)

    const url = reviewUrl || storeUrl

    if (target === TARGET.IOS || target === TARGET.ANDROID) {
      try {
        const { AppReview } = await import('@capawesome/capacitor-app-review')
        await AppReview.requestReview()
      } catch {
        // OS declined to show the sheet (rate-limited) -> fall back to the store
        try {
          const { AppReview } = await import('@capawesome/capacitor-app-review')
          await AppReview.openAppStore({ appId: IOS_APP_ID })
        } catch {
          openInSystemBrowser(url)
        }
      }
      return
    }

    openInSystemBrowser(url)
  }

  // Negative answer -> route to feedback (or just close). We do NOT mark the
  // target completed here: a "Not really" / "No thanks" is not a finished review,
  // so the user can be re-asked later (subject to the cooldown + MAX_SHOWS cap).
  // Only a positive answer (accept) completes the target.
  async function decline({ target, feedback, trigger }) {
    bus.emit(EVENTS.DECLINED, { target, trigger, feedback: !!feedback })
    if (feedback) openExternalUrl(`${CONFIG.landingpageUrl}/contact`)
  }

  // Acts on an answer from either surface. `null` = dismissed, which needs no
  // outcome — the show was already recorded.
  async function applyResult(resolved, trigger, result) {
    if (result === RATING_RESULT.POSITIVE) await accept(resolved, trigger)
    else if (result === RATING_RESULT.FEEDBACK) await decline({ target: resolved.target, trigger, feedback: true })
    else if (result === RATING_RESULT.NEGATIVE) await decline({ target: resolved.target, trigger, feedback: false })
  }

  // --- modal surface (all clients) -------------------------------------------

  // Resolves the target, gates, opens the RatingPromptDialog and acts on the
  // result. Returns true if the dialog was shown.
  async function openModalFlow(trigger) {
    // One ask at a time: a second flow would replace the first one's dialog
    // channel and leave its promise unsettled.
    if (dialogOpen.value) return false
    if (!isSurfaceVisible()) return false

    const resolved = resolveTarget()
    if (!resolved) return false
    if (!(await shouldPrompt(resolved.target))) return false

    // On the plain webapp we only nudge users who actually have the matching
    // extension installed (the prompt sends them to that extension's store).
    if (isWebApp.value && !(await checkPresence())) return false

    // The show is recorded when the dialog reports that it rendered, so a
    // surface that never gets to paint cannot burn a show or the cooldown.
    let shownRecorded = Promise.resolve()
    const result = await openRatingDialog(() => {
      shownRecorded = recordShown(resolved.target, trigger)
    })

    // Settle the show before acting on the answer: both read-modify-write the
    // same settings blob, so a positive answer landing first would drop the
    // shows/lastShownAt it wrote.
    await shownRecorded.catch(() => {})
    await applyResult(resolved, trigger, result)
    return true
  }

  // --- queued trigger (survives the context that queued it) ------------------

  // Queue the prompt without showing it. Used by surfaces that must not display
  // it in place (the add screens) -> flushed once the user is somewhere it can
  // be seen. Only native queues: the other clients use the banner instead.
  async function requestRatingPrompt(trigger) {
    if (!isNative.value) return
    await Preferences.set(PENDING_TRIGGER_KEY, { trigger, queuedAt: Date.now() })
  }

  // Show a queued prompt, if any. Called on route change and on resume, so it
  // lands on a live surface even when the queueing one is already gone.
  async function flushPendingRating() {
    if (flushing || !isNative.value) return false

    // Claimed synchronously: a route change and a resume routinely fire back to
    // back, and if both got past this the second would replace the first one's
    // dialog channel and leave its flow hanging forever.
    flushing = true
    try {
      const pending = await Preferences.get(PENDING_TRIGGER_KEY)
      if (!pending?.trigger) return false

      if (Date.now() - (pending.queuedAt ?? 0) > PENDING_TRIGGER_TTL_MS) {
        await Preferences.remove(PENDING_TRIGGER_KEY)
        return false
      }

      // Leave it queued: a hidden surface is usually one on its way out, and the
      // context that replaces it will flush the same entry.
      if (!isSurfaceVisible()) return false

      await Preferences.remove(PENDING_TRIGGER_KEY)
      return await openModalFlow(pending.trigger)
    } finally {
      flushing = false
    }
  }

  // --- ask on open (extension + webapp) --------------------------------------

  // The clients with no native review sheet have no equivalent "you just
  // finished something" moment to hang the ask on — the extension add flow ends
  // by closing its own window — so they ask on open instead, gated exactly the
  // same way.
  async function promptOnAppOpen() {
    if (isNative.value) return false
    return openModalFlow(RATING_TRIGGER.APP_OPEN)
  }

  return {
    shouldPrompt,
    // native: queued by an add, flushed onto a visible surface
    requestRatingPrompt,
    flushPendingRating,
    // extension + webapp
    promptOnAppOpen,
  }
}
